// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import {
  ActionIcon,
  Box,
  Button,
  Group,
  Loader,
  Menu,
  Modal,
  Paper,
  Stack,
  Text,
  Title,
  UnstyledButton,
} from '@mantine/core';
import { createReference, formatHumanName, getReferenceString } from '@medplum/core';
import type { Appointment, Practitioner, Schedule, Slot } from '@medplum/fhirtypes';
import { useMedplum, useMedplumProfile } from '@medplum/react';
import { getTimezoneExtensionValue, isValidTimezone } from '@polarissleep/scheduling-next';
import { IconChevronDown, IconChevronLeft, IconChevronRight } from '@tabler/icons-react';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import localeData from 'dayjs/plugin/localeData';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import minMax from 'dayjs/plugin/minMax';
import utc from 'dayjs/plugin/utc';
import type { CSSProperties, JSX } from 'react';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { Event, Formats, SlotInfo, ToolbarProps, View } from 'react-big-calendar';
import { Calendar, dayjsLocalizer } from 'react-big-calendar';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { getProviderAppEnvConfig } from '../../config/env';
import { useActivePractitioners } from '../../hooks/useActivePractitioners';
import { useAppointmentTypes } from '../../hooks/useAppointmentTypes';
import { expandScheduleAvailability, getSchedulingParameters } from '../../scheduling-next/availability';
import { POLARIS_ENV_TAG_SYSTEM, V2_OVERRIDE_TAG_CODE, V2_OVERRIDE_TAG_SYSTEM } from '../../scheduling-next/constants';
import classes from './AppointmentsCalendarCard.module.css';

configureCalendarDayjs();

const CALENDAR_LOCALIZER = dayjsLocalizer(createCalendarDayjs());
const CALENDAR_FORMATS: Formats = {
  timeGutterFormat: (time) => dayjs(time).format('ha'),
};

const MONTH_SUMMARY_BUTTON_STYLE: CSSProperties = {
  background: 'transparent',
  border: 0,
  color: 'inherit',
  cursor: 'pointer',
  font: 'inherit',
  fontWeight: 700,
  lineHeight: 1.2,
  padding: 0,
  textAlign: 'left',
  width: '100%',
};

type AppointmentEvent = Event & { type: 'appointment'; appointment: Appointment; start: Date; end: Date };
type DaySummaryEvent = Event & {
  type: 'day-summary';
  appointments: AppointmentEvent[];
  start: Date;
  end: Date;
  title: string;
};
type SlotEvent = Event & { type: 'slot'; status: string; start: Date; end: Date };
type CalendarEvent = AppointmentEvent | DaySummaryEvent | SlotEvent;
type CalendarFilterOption = { value: string; label: string };

interface AppointmentsCalendarCardProps {
  title: string;
  canViewClinicAppointments?: boolean;
  includeAvailability?: boolean;
  showCreateAppointment?: boolean;
  onCreateAppointment?: () => void;
  showEditAvailability?: boolean;
  onEditAvailability?: () => void;
  onAppointmentSelect?: (appointment: Appointment) => void;
  onSlotSelect?: (slot: SlotInfo) => void;
  refreshToken?: number;
  calendarHeight?: number;
}

const CALENDAR_LOAD_ATTEMPTS = 2;
const CALENDAR_RETRY_DELAY_MS = 250;

async function retryCalendarLoad<T>(operation: () => Promise<T>): Promise<T> {
  let lastError: unknown;
  for (let attempt = 0; attempt < CALENDAR_LOAD_ATTEMPTS; attempt += 1) {
    try {
      return await operation();
    } catch (err: unknown) {
      lastError = err;
      if (attempt + 1 < CALENDAR_LOAD_ATTEMPTS) {
        await new Promise<void>((resolve) => window.setTimeout(resolve, CALENDAR_RETRY_DELAY_MS));
      }
    }
  }
  throw lastError instanceof Error ? lastError : new Error('Failed to load calendar availability.');
}

export function AppointmentsCalendarCard({
  title,
  canViewClinicAppointments = false,
  includeAvailability = false,
  showCreateAppointment = false,
  onCreateAppointment,
  showEditAvailability = false,
  onEditAvailability,
  onAppointmentSelect,
  onSlotSelect,
  refreshToken = 0,
  calendarHeight = 640,
}: AppointmentsCalendarCardProps): JSX.Element {
  const medplum = useMedplum();
  const profile = useMedplumProfile() as Practitioner | undefined;
  const { appointmentTypes } = useAppointmentTypes();
  const { practitioners } = useActivePractitioners();

  const calendarRef = useRef<Calendar<CalendarEvent>>(null);
  const calendarViewportRef = useRef<HTMLDivElement>(null);
  const [schedule, setSchedule] = useState<Schedule | undefined>();
  const [view, setView] = useState<View>('week');
  const [date, setDate] = useState<Date>(new Date());
  const [range, setRange] = useState<{ start: Date; end: Date } | undefined>(undefined);
  const [appointmentEvents, setAppointmentEvents] = useState<AppointmentEvent[]>([]);
  const [slotEvents, setSlotEvents] = useState<SlotEvent[]>();
  const [loading, setLoading] = useState(true);
  const [selectedType, setSelectedType] = useState<string>('all');
  const [selectedProviderId, setSelectedProviderId] = useState<string>(canViewClinicAppointments ? 'all' : 'me');
  const [selectedAppointmentDate, setSelectedAppointmentDate] = useState<Date | undefined>();

  const profileRef = profile ? (getReferenceString(profile) as string) : undefined;
  const profileId = profile?.id;
  const profileName = profile?.name?.[0] ? formatHumanName(profile.name[0]) : 'My appointments';
  const clinicViewEnabled = canViewClinicAppointments;
  const { medplumOrganizationId, polarisEnv, schedulingEngine } = getProviderAppEnvConfig();
  const selectedAvailabilityPractitioner = clinicViewEnabled
    ? practitioners.find((practitioner) => practitioner.value === selectedProviderId)?.practitioner
    : profile;
  const availabilityEnabled = includeAvailability && (!clinicViewEnabled || selectedProviderId !== 'all');

  useEffect(() => {
    if (clinicViewEnabled) {
      setSelectedProviderId('all');
      return;
    }

    if (profileId) {
      setSelectedProviderId(profileId);
    }
  }, [clinicViewEnabled, profileId]);

  useEffect(() => {
    setRange(getVisibleRange(date, view));
  }, [date, view]);

  useEffect(() => {
    if (!availabilityEnabled || medplum.isLoading() || !selectedAvailabilityPractitioner) {
      setSchedule(undefined);
      return;
    }

    retryCalendarLoad(() =>
      medplum.searchOne(
        'Schedule',
        schedulingEngine === 'medplum-v2'
          ? {
              actor: getReferenceString(selectedAvailabilityPractitioner),
              active: 'true',
              _compartment: `Organization/${medplumOrganizationId ?? ''}`,
              _tag: `${POLARIS_ENV_TAG_SYSTEM}|${polarisEnv ?? ''}`,
            }
          : { actor: getReferenceString(selectedAvailabilityPractitioner) }
      )
    )
      .then((foundSchedule) => foundSchedule as Schedule | undefined)
      .then((foundSchedule) => {
        if (foundSchedule) {
          setSchedule(foundSchedule);
          return;
        }
        if (schedulingEngine === 'medplum-v2') {
          return undefined;
        }
        return medplum.createResource<Schedule>({
          resourceType: 'Schedule',
          actor: [createReference(selectedAvailabilityPractitioner)],
          active: true,
        });
      })
      .then((createdSchedule) => {
        if (createdSchedule) {
          setSchedule(createdSchedule);
        }
      })
      .catch(console.error);
  }, [
    availabilityEnabled,
    medplum,
    medplumOrganizationId,
    polarisEnv,
    schedulingEngine,
    selectedAvailabilityPractitioner,
  ]);

  const handleRangeChange = useCallback(
    (newRange: Date[] | { start: Date; end: Date }) => {
      const nextRange = Array.isArray(newRange)
        ? {
            start: newRange[0],
            end: new Date(newRange[newRange.length - 1].getTime() + 24 * 60 * 60 * 1000),
          }
        : newRange;

      if (nextRange.start.getTime() !== range?.start.getTime() || nextRange.end.getTime() !== range?.end.getTime()) {
        setRange(nextRange);
      }
    },
    [range]
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: refreshToken intentionally forces a reload after cancellation.
  const fetchEvents = useCallback(async () => {
    if (!range || (!clinicViewEnabled && !profileRef)) {
      setLoading(false);
      return;
    }

    setLoading(true);

    const start = range.start.toISOString();
    const end = range.end.toISOString();

    const appointmentParams: [string, string][] = [
      ['_count', '1000'],
      ['date', `ge${start}`],
      ['date', `le${end}`],
    ];

    let providerRef: string | undefined;
    if (clinicViewEnabled && selectedProviderId !== 'all') {
      providerRef = `Practitioner/${selectedProviderId}`;
    } else if (!clinicViewEnabled && profileRef) {
      providerRef = profileRef;
    }

    if (providerRef) {
      appointmentParams.splice(1, 0, ['actor', providerRef]);
    }

    try {
      const tasks: Promise<unknown>[] = [
        retryCalendarLoad(() => medplum.searchResources('Appointment', appointmentParams)).then((appointments) =>
          setAppointmentEvents(appointmentsToEvents(appointments, clinicViewEnabled))
        ),
      ];

      if (availabilityEnabled && schedule && selectedAvailabilityPractitioner) {
        // v2 recurring availability comes from Schedule and stays virtual. Exception
        // Slots are layered on top; calendar reads never reconcile or materialize Slots.
        const slotSearch: [string, string][] = [
          ['_count', '1000'],
          ['schedule', getReferenceString(schedule) as string],
          ['start', `ge${start}`],
          ['start', `le${end}`],
        ];
        if (schedulingEngine === 'medplum-v2') {
          slotSearch.push(
            ['_tag', `${V2_OVERRIDE_TAG_SYSTEM}|${V2_OVERRIDE_TAG_CODE}`],
            ['_tag', `${POLARIS_ENV_TAG_SYSTEM}|${getProviderAppEnvConfig().polarisEnv ?? ''}`]
          );
        }
        const recurringEvents =
          schedulingEngine === 'medplum-v2'
            ? scheduleAvailabilityToEvents(schedule, selectedAvailabilityPractitioner, range)
            : ([] as SlotEvent[]);
        tasks.push(
          retryCalendarLoad(() => medplum.searchResources('Slot', slotSearch)).then((slots) =>
            setSlotEvents([...recurringEvents, ...slotsToEvents(slots)])
          )
        );
      } else {
        setSlotEvents(undefined);
      }

      await Promise.all(tasks);
    } catch (err) {
      console.error('Failed to load calendar events', err);
    } finally {
      setLoading(false);
    }
  }, [
    availabilityEnabled,
    medplum,
    selectedAvailabilityPractitioner,
    profileRef,
    range,
    schedule,
    schedulingEngine,
    refreshToken,
    selectedProviderId,
  ]);

  useEffect(() => {
    fetchEvents().catch(console.error);
  }, [fetchEvents]);

  const resetCalendarScroll = useCallback(() => {
    const viewport = calendarViewportRef.current?.querySelector<HTMLElement>('.rbc-time-content');
    if (viewport) {
      viewport.scrollTop = 0;
    }
  }, []);

  const scheduleCalendarScrollReset = useCallback(() => {
    const animationFrameIds: number[] = [];
    const timeoutIds: number[] = [];
    const queueAnimationFrame = (callback: FrameRequestCallback): void => {
      animationFrameIds.push(window.requestAnimationFrame(callback));
    };

    resetCalendarScroll();
    queueAnimationFrame(() => {
      resetCalendarScroll();
      queueAnimationFrame(resetCalendarScroll);
    });
    timeoutIds.push(window.setTimeout(resetCalendarScroll, 100));
    timeoutIds.push(window.setTimeout(resetCalendarScroll, 300));

    return () => {
      for (const id of animationFrameIds) {
        window.cancelAnimationFrame(id);
      }
      for (const id of timeoutIds) {
        window.clearTimeout(id);
      }
    };
  }, [resetCalendarScroll]);

  useLayoutEffect(() => {
    if (loading) {
      return;
    }

    return scheduleCalendarScrollReset();
  }, [loading, scheduleCalendarScrollReset]);

  const filteredAppointmentEvents = useMemo(() => {
    if (selectedType === 'all') {
      return appointmentEvents;
    }
    return appointmentEvents.filter((event) => {
      const coding = event.appointment.appointmentType?.coding?.[0];
      return coding?.code === selectedType;
    });
  }, [appointmentEvents, selectedType]);

  const appointmentEventsByDay = useMemo(
    () => groupAppointmentEventsByDay(filteredAppointmentEvents),
    [filteredAppointmentEvents]
  );

  const calendarEvents = useMemo<CalendarEvent[]>(() => {
    if (view !== 'month') {
      return filteredAppointmentEvents;
    }
    return collapseMonthAppointmentEvents(appointmentEventsByDay);
  }, [appointmentEventsByDay, filteredAppointmentEvents, view]);

  const selectedDayAppointmentEvents = useMemo(() => {
    if (!selectedAppointmentDate) {
      return [];
    }
    return getAppointmentEventsForDate(appointmentEventsByDay, selectedAppointmentDate);
  }, [appointmentEventsByDay, selectedAppointmentDate]);

  const openDayAppointments = useCallback(
    (day: Date): boolean => {
      const dayAppointments = getAppointmentEventsForDate(appointmentEventsByDay, day);
      if (dayAppointments.length <= 1) {
        return false;
      }
      setSelectedAppointmentDate(dayjs(day).startOf('day').toDate());
      return true;
    },
    [appointmentEventsByDay]
  );

  const closeDayAppointmentsModal = useCallback(() => {
    setSelectedAppointmentDate(undefined);
  }, []);

  const handleDayAppointmentSelect = useCallback(
    (appointment: Appointment) => {
      closeDayAppointmentsModal();
      onAppointmentSelect?.(appointment);
    },
    [closeDayAppointmentsModal, onAppointmentSelect]
  );

  const handleCalendarEventSelect = useCallback(
    (event: CalendarEvent) => {
      if (event.type === 'day-summary') {
        setSelectedAppointmentDate(dayjs(event.start).startOf('day').toDate());
        return;
      }

      if (event.type === 'appointment') {
        if (view === 'month' && openDayAppointments(event.start)) {
          return;
        }
        onAppointmentSelect?.(event.appointment);
      }
    },
    [onAppointmentSelect, openDayAppointments, view]
  );

  const handleCalendarSlotSelect = useCallback(
    (slot: SlotInfo) => {
      if (view === 'month' && openDayAppointments(slot.start)) {
        return;
      }

      if (availabilityEnabled) {
        onSlotSelect?.(slot);
      }
    },
    [availabilityEnabled, onSlotSelect, openDayAppointments, view]
  );

  const handleCalendarDrillDown = useCallback(
    (drillDownDate: Date, drillDownView?: View) => {
      if (view === 'month' && openDayAppointments(drillDownDate)) {
        return;
      }

      setDate(drillDownDate);
      setView(drillDownView && drillDownView !== view ? drillDownView : 'day');
      scheduleCalendarScrollReset();
    },
    [openDayAppointments, scheduleCalendarScrollReset, view]
  );

  const calendarSelectable = view === 'month' || availabilityEnabled;

  const typeOptions = useMemo(
    () => [
      { value: 'all', label: 'All Types' },
      ...appointmentTypes
        .filter((type) => Boolean(type.coding.code))
        .map((type) => ({
          value: type.coding.code as string,
          label: type.display || type.coding.display || type.coding.code || 'Unnamed type',
        })),
    ],
    [appointmentTypes]
  );

  const providerOptions = useMemo(() => {
    if (!clinicViewEnabled) {
      return [{ value: profileId ?? 'me', label: profileName }];
    }
    return [
      { value: 'all', label: 'All Providers' },
      ...practitioners.map((practitioner) => ({
        value: practitioner.value,
        label: practitioner.label,
      })),
    ];
  }, [clinicViewEnabled, practitioners, profileId, profileName]);

  const toolbar = (props: ToolbarProps<CalendarEvent>): JSX.Element => {
    return (
      <Box className={classes.toolbar}>
        <Box className={classes.toolbarInner}>
          <ActionIcon
            variant="subtle"
            color="gray"
            size="lg"
            radius="xl"
            aria-label="Previous range"
            onClick={() => props.onNavigate('PREV')}
          >
            <IconChevronLeft size={20} />
          </ActionIcon>

          <Title order={3} className={classes.toolbarTitle}>
            {formatToolbarLabel(props.date, props.view)}
          </Title>

          <ActionIcon
            variant="subtle"
            color="gray"
            size="lg"
            radius="xl"
            aria-label="Next range"
            onClick={() => props.onNavigate('NEXT')}
          >
            <IconChevronRight size={20} />
          </ActionIcon>
        </Box>
      </Box>
    );
  };

  const calendarEventContent = useCallback(
    ({ event }: { event: CalendarEvent }) => (
      <CalendarEventContent event={event} onOpenDayAppointments={openDayAppointments} />
    ),
    [openDayAppointments]
  );

  const calendarComponents =
    view === 'week'
      ? { toolbar, header: CalendarDayHeader, event: calendarEventContent }
      : { toolbar, event: calendarEventContent };
  const selectedTypeLabel = getSelectedLabel(typeOptions, selectedType, 'Type');
  const selectedProviderLabel = getSelectedLabel(providerOptions, selectedProviderId, 'Provider');
  const typeButtonLabel = selectedType === 'all' ? 'Type' : selectedTypeLabel;
  const providerButtonLabel = selectedProviderId === 'all' ? 'Provider' : selectedProviderLabel;
  const calendarMinTime = useMemo(() => dayjs(date).hour(8).minute(0).second(0).millisecond(0).toDate(), [date]);
  const calendarMaxTime = useMemo(() => dayjs(date).hour(22).minute(0).second(0).millisecond(0).toDate(), [date]);
  const calendarRenderKey = `${view}-${dayjs(date).format('YYYY-MM-DD')}-${selectedProviderId}`;
  const viewOptions = [
    { value: 'day', label: 'Day View' },
    { value: 'week', label: 'Week View' },
    { value: 'month', label: 'Month View' },
  ];
  const viewButtonLabel = view === 'week' ? 'Date Range' : getSelectedLabel(viewOptions, view, 'Date Range');

  return (
    <Paper shadow="sm" withBorder p="lg" radius="md" bg="white" className={classes.card}>
      <Stack gap="md">
        <Group justify="space-between" align="center" wrap="wrap" className={classes.cardHeader}>
          <Stack gap={2}>
            <Title order={3} size="h2">
              {title}
            </Title>
          </Stack>

          <Group gap="xs" wrap="wrap" justify="flex-end" className={classes.controls}>
            <CalendarFilterMenu
              label={typeButtonLabel}
              width={88}
              options={typeOptions}
              value={selectedType}
              onChange={setSelectedType}
            />
            {canViewClinicAppointments && (
              <CalendarFilterMenu
                label={providerButtonLabel}
                width={108}
                options={providerOptions}
                value={selectedProviderId}
                onChange={(value) => {
                  setSelectedProviderId(value);
                  scheduleCalendarScrollReset();
                }}
              />
            )}
            <CalendarFilterMenu
              label={viewButtonLabel}
              width={127}
              options={viewOptions}
              value={view}
              onChange={(value) => {
                setView(value as View);
                scheduleCalendarScrollReset();
              }}
            />
            {showEditAvailability && (
              <Button size="sm" className={classes.secondaryButton} onClick={onEditAvailability}>
                Edit Availability
              </Button>
            )}
            {showCreateAppointment && (
              <Button size="sm" className={classes.primaryButton} onClick={onCreateAppointment}>
                Create Appointment
              </Button>
            )}
          </Group>
        </Group>

        <Box className={classes.calendarFrame}>
          {loading ? (
            <Stack align="center" justify="center" h={calendarHeight}>
              <Loader size="sm" />
              <Text size="sm" c="dimmed">
                Loading calendar...
              </Text>
            </Stack>
          ) : (
            <Box ref={calendarViewportRef} style={{ height: calendarHeight }}>
              <Calendar
                key={calendarRenderKey}
                ref={calendarRef}
                components={calendarComponents}
                formats={CALENDAR_FORMATS}
                view={view}
                date={date}
                localizer={CALENDAR_LOCALIZER}
                events={calendarEvents}
                backgroundEvents={slotEvents}
                min={calendarMinTime}
                max={calendarMaxTime}
                step={60}
                timeslots={1}
                onNavigate={(newDate: Date, newView: View) => {
                  setDate(newDate);
                  setView(newView);
                  scheduleCalendarScrollReset();
                }}
                onView={(newView: View) => {
                  setView(newView);
                  scheduleCalendarScrollReset();
                }}
                onRangeChange={handleRangeChange}
                onSelectSlot={handleCalendarSlotSelect}
                onSelectEvent={handleCalendarEventSelect}
                onDrillDown={handleCalendarDrillDown}
                scrollToTime={calendarMinTime}
                enableAutoScroll
                style={{ height: calendarHeight - 20 }}
                selectable={calendarSelectable}
                eventPropGetter={eventPropGetter}
              />
            </Box>
          )}
        </Box>

        <DayAppointmentsModal
          opened={Boolean(selectedAppointmentDate)}
          date={selectedAppointmentDate}
          appointments={selectedDayAppointmentEvents}
          onClose={closeDayAppointmentsModal}
          onAppointmentSelect={handleDayAppointmentSelect}
        />
      </Stack>
    </Paper>
  );
}

type CalendarDayjsFactory = typeof dayjs & { tz?: unknown };

function configureCalendarDayjs(): void {
  dayjs.extend(isBetween);
  dayjs.extend(isSameOrAfter);
  dayjs.extend(isSameOrBefore);
  dayjs.extend(localeData);
  dayjs.extend(localizedFormat);
  dayjs.extend(minMax);
  dayjs.extend(utc);
}

function createCalendarDayjs(): typeof dayjs {
  const localDayjs = ((...args: Parameters<typeof dayjs>) => dayjs(...args)) as CalendarDayjsFactory;
  let calendarDayjs: CalendarDayjsFactory;

  const hiddenStatics = new Set<PropertyKey>(['tz']);
  const dayjsStatics = dayjs as unknown as Record<PropertyKey, unknown>;

  calendarDayjs = new Proxy(localDayjs, {
    get(_target, property) {
      if (hiddenStatics.has(property)) {
        return undefined;
      }

      if (property === 'extend') {
        return (plugin: Parameters<typeof dayjs.extend>[0], option?: Parameters<typeof dayjs.extend>[1]) => {
          dayjs.extend(plugin, option);
          return calendarDayjs;
        };
      }

      const value = dayjsStatics[property];
      return typeof value === 'function' ? value.bind(dayjs) : value;
    },
    has(_target, property) {
      return !hiddenStatics.has(property) && property in dayjs;
    },
  }) as CalendarDayjsFactory;

  return calendarDayjs as typeof dayjs;
}

function CalendarDayHeader({ date }: { date: Date; label: string }): JSX.Element {
  return (
    <div className={classes.dayHeader}>
      <div className={classes.dayName}>{dayjs(date).format('dddd')}</div>
      <div className={classes.dayNumber}>{dayjs(date).format('D')}</div>
    </div>
  );
}

function CalendarEventContent({
  event,
  onOpenDayAppointments,
}: {
  event: CalendarEvent;
  onOpenDayAppointments?: (day: Date) => boolean;
}): JSX.Element {
  if (event.type === 'day-summary') {
    return (
      <button
        type="button"
        style={MONTH_SUMMARY_BUTTON_STYLE}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onOpenDayAppointments?.(event.start);
        }}
      >
        {event.title}
      </button>
    );
  }

  if (event.type === 'slot') {
    return <span>{event.title}</span>;
  }

  return (
    <div className={classes.eventContent}>
      <div className={classes.eventTitle}>{event.title}</div>
      <div className={classes.eventTime}>{formatEventTime(event.start, event.end)}</div>
    </div>
  );
}

function DayAppointmentsModal({
  opened,
  date,
  appointments,
  onClose,
  onAppointmentSelect,
}: {
  opened: boolean;
  date?: Date;
  appointments: AppointmentEvent[];
  onClose: () => void;
  onAppointmentSelect: (appointment: Appointment) => void;
}): JSX.Element {
  const modalTitle = date ? `Appointments on ${dayjs(date).format('MMMM D, YYYY')}` : 'Appointments';

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Stack gap={0}>
          <Text fw={700}>{modalTitle}</Text>
          <Text size="sm" c="dimmed">
            {formatAppointmentCount(appointments.length)}
          </Text>
        </Stack>
      }
      size="lg"
      centered
    >
      {appointments.length === 0 ? (
        <Text size="sm" c="dimmed">
          No appointments found for this day.
        </Text>
      ) : (
        <Box mah={480} style={{ overflowY: 'auto' }}>
          <Stack gap="sm" pr="xs">
            {appointments.map((event) => {
              const appointmentTypeLabel = getAppointmentTypeLabel(event.appointment);

              return (
                <UnstyledButton
                  key={getAppointmentEventKey(event)}
                  type="button"
                  onClick={() => onAppointmentSelect(event.appointment)}
                  style={{ display: 'block', width: '100%' }}
                >
                  <Paper withBorder radius="md" p="sm">
                    <Group justify="space-between" align="flex-start" wrap="nowrap" gap="md">
                      <Stack gap={3}>
                        <Text fw={600}>{getAppointmentEventTitle(event)}</Text>
                        <Text size="sm" c="dimmed">
                          {formatDetailedEventTime(event.start, event.end)}
                        </Text>
                        {appointmentTypeLabel && (
                          <Text size="xs" c="dimmed">
                            {appointmentTypeLabel}
                          </Text>
                        )}
                      </Stack>
                      {event.appointment.status && (
                        <Text size="xs" c="dimmed" tt="capitalize" style={{ whiteSpace: 'nowrap' }}>
                          {event.appointment.status.replace(/-/g, ' ')}
                        </Text>
                      )}
                    </Group>
                  </Paper>
                </UnstyledButton>
              );
            })}
          </Stack>
        </Box>
      )}
    </Modal>
  );
}

function CalendarFilterMenu({
  label,
  options,
  value,
  width,
  onChange,
}: {
  label: string;
  options: CalendarFilterOption[];
  value: string;
  width: number;
  onChange: (value: string) => void;
}): JSX.Element {
  return (
    <Menu shadow="md" width={220} withinPortal>
      <Menu.Target>
        <Button
          variant="outline"
          color="dark"
          size="sm"
          className={classes.filterButton}
          rightSection={<IconChevronDown size={15} />}
          style={{ width }}
        >
          {label}
        </Button>
      </Menu.Target>
      <Menu.Dropdown>
        {options.map((option) => (
          <Menu.Item key={option.value} onClick={() => onChange(option.value)} fw={option.value === value ? 700 : 400}>
            {option.label}
          </Menu.Item>
        ))}
      </Menu.Dropdown>
    </Menu>
  );
}

function getSelectedLabel(options: CalendarFilterOption[], value: string, fallback: string): string {
  return options.find((option) => option.value === value)?.label ?? fallback;
}

function formatToolbarLabel(date: Date, view: View): string {
  if (view === 'day') {
    return dayjs(date).format('MMMM D, YYYY');
  }
  if (view === 'month') {
    return dayjs(date).format('MMMM YYYY');
  }
  const weekStart = dayjs(date).startOf('week');
  const weekEnd = dayjs(date).endOf('week');
  if (weekStart.month() === weekEnd.month()) {
    return `${weekStart.format('MMMM D')}–${weekEnd.format('D, YYYY')}`;
  }
  return `${weekStart.format('MMMM D')} – ${weekEnd.format('MMMM D, YYYY')}`;
}

function formatEventTime(start: Date, end: Date): string {
  const startLabel = dayjs(start).format('h');
  const startMeridiem = dayjs(start).format('a');
  const endLabel = dayjs(end).format('h');
  const endMeridiem = dayjs(end).format('a');

  if (startMeridiem === endMeridiem) {
    return `${startLabel}–${endLabel}${endMeridiem}`;
  }

  return `${startLabel}${startMeridiem}–${endLabel}${endMeridiem}`;
}

function formatDetailedEventTime(start: Date, end: Date): string {
  return `${dayjs(start).format('h:mm a')} – ${dayjs(end).format('h:mm a')}`;
}

function formatAppointmentCount(count: number): string {
  return `${count} appointment${count === 1 ? '' : 's'}`;
}

function getAppointmentEventTitle(event: AppointmentEvent): string {
  return typeof event.title === 'string' ? event.title : 'Appointment';
}

function getAppointmentTypeLabel(appointment: Appointment): string | undefined {
  const coding = appointment.appointmentType?.coding?.[0];
  return appointment.appointmentType?.text ?? coding?.display ?? coding?.code;
}

function getAppointmentEventKey(event: AppointmentEvent): string {
  return (
    event.appointment.id ?? `${event.start.toISOString()}-${event.end.toISOString()}-${getAppointmentEventTitle(event)}`
  );
}

function getDayKey(date: Date): string {
  return dayjs(date).format('YYYY-MM-DD');
}

function getAppointmentEventsForDate(eventsByDay: Map<string, AppointmentEvent[]>, date: Date): AppointmentEvent[] {
  return eventsByDay.get(getDayKey(date)) ?? [];
}

function groupAppointmentEventsByDay(events: AppointmentEvent[]): Map<string, AppointmentEvent[]> {
  const grouped = new Map<string, AppointmentEvent[]>();

  for (const event of events) {
    const dayKey = getDayKey(event.start);
    const dayEvents = grouped.get(dayKey) ?? [];
    dayEvents.push(event);
    grouped.set(dayKey, dayEvents);
  }

  for (const dayEvents of grouped.values()) {
    dayEvents.sort(compareAppointmentEvents);
  }

  return grouped;
}

function collapseMonthAppointmentEvents(eventsByDay: Map<string, AppointmentEvent[]>): CalendarEvent[] {
  const collapsed: CalendarEvent[] = [];

  for (const dayEvents of eventsByDay.values()) {
    if (dayEvents.length <= 1) {
      collapsed.push(...dayEvents);
      continue;
    }

    const firstEvent = dayEvents[0];
    collapsed.push({
      type: 'day-summary',
      appointments: dayEvents,
      title: formatAppointmentCount(dayEvents.length),
      start: firstEvent.start,
      end: firstEvent.end,
      resource: { appointments: dayEvents.map((event) => event.appointment) },
    });
  }

  return collapsed.sort(compareCalendarEvents);
}

function compareCalendarEvents(a: CalendarEvent, b: CalendarEvent): number {
  const startDiff = a.start.getTime() - b.start.getTime();
  if (startDiff !== 0) {
    return startDiff;
  }

  return a.end.getTime() - b.end.getTime();
}

function compareAppointmentEvents(a: AppointmentEvent, b: AppointmentEvent): number {
  const timeDiff = compareCalendarEvents(a, b);
  if (timeDiff !== 0) {
    return timeDiff;
  }

  return getAppointmentEventKey(a).localeCompare(getAppointmentEventKey(b));
}

function getVisibleRange(date: Date, view: View): { start: Date; end: Date } {
  if (view === 'day') {
    const start = dayjs(date).startOf('day').toDate();
    const end = dayjs(date).endOf('day').toDate();
    return { start, end };
  }

  if (view === 'month') {
    const start = dayjs(date).startOf('month').startOf('week').toDate();
    const end = dayjs(date).endOf('month').endOf('week').toDate();
    return { start, end };
  }

  const start = dayjs(date).startOf('week').toDate();
  const end = dayjs(date).endOf('week').toDate();
  return { start, end };
}

function eventPropGetter(
  event: CalendarEvent,
  _start: Date,
  _end: Date,
  _isSelected: boolean
): { className?: string; style?: CSSProperties } {
  const style: CSSProperties = {
    backgroundColor: '#230415',
    border: '1px solid rgba(255, 255, 255, 0)',
    borderRadius: '4px',
    color: 'white',
    display: 'block',
    opacity: 1,
  };

  if (event.type === 'day-summary') {
    style.cursor = 'pointer';
  }

  if (event.type === 'slot') {
    style.backgroundColor = event.status === 'free' ? '#fef0f5' : '#ced4da';
    style.color = '#230415';
    style.opacity = 0.6;
  }

  return { style };
}

function appointmentsToEvents(appointments: Appointment[], clinicViewEnabled: boolean): AppointmentEvent[] {
  return appointments
    .filter((appointment) => appointment.status !== 'cancelled')
    .map((appointment) => {
      const patientParticipant = appointment.participant?.find((participant) =>
        participant.actor?.reference?.startsWith('Patient/')
      );
      const providerParticipant = appointment.participant?.find((participant) =>
        participant.actor?.reference?.startsWith('Practitioner/')
      );
      const status = !['booked', 'fulfilled'].includes(appointment.status as string) ? ` (${appointment.status})` : '';
      const providerLabel =
        clinicViewEnabled && providerParticipant?.actor?.display ? ` - ${providerParticipant.actor.display}` : '';

      return {
        type: 'appointment',
        appointment,
        title: `${patientParticipant?.actor?.display ?? 'Patient'}${providerLabel}${status}`,
        start: new Date(appointment.start as string),
        end: new Date(appointment.end as string),
        resource: appointment,
      };
    });
}

function slotsToEvents(slots: Slot[]): SlotEvent[] {
  if (slots.length === 0) {
    return [];
  }

  const filteredSlots = slots.filter((slot) => slot.status === 'free' || slot.status === 'busy-unavailable');
  return collapseSlotEvents(
    filteredSlots.map((slot) => ({
      type: 'slot',
      status: slot.status,
      start: new Date(slot.start),
      end: new Date(slot.end),
    }))
  );
}

function scheduleAvailabilityToEvents(
  schedule: Schedule,
  actor: Practitioner,
  range: { start: Date; end: Date }
): SlotEvent[] {
  const serviceIds = getScheduleServiceIds(schedule);
  const events: SlotEvent[] = [];
  const actorTimezone = getTimezoneExtensionValue(actor.extension);

  for (const serviceId of serviceIds) {
    const timezone = getScheduleTimezone(schedule, serviceId) ?? actorTimezone;
    if (!timezone || !isValidTimezone(timezone)) {
      continue;
    }
    const windows = expandScheduleAvailability(schedule, serviceId, timezone, range);
    events.push(
      ...windows.map(({ start, end }) => ({
        type: 'slot' as const,
        status: 'free',
        start,
        end,
        title: 'Available',
        resource: { status: 'free', recurring: true },
      }))
    );
  }

  return collapseSlotEvents(events);
}

function getScheduleServiceIds(schedule: Schedule): string[] {
  const serviceIds = schedule.serviceType?.flatMap((serviceType) =>
    (serviceType.extension ?? [])
      .filter((extension) => extension.url === 'https://medplum.com/fhir/service-type-reference')
      .map((extension) => extension.valueReference?.reference?.replace('HealthcareService/', ''))
      .filter((id): id is string => Boolean(id))
  );
  return [...new Set(serviceIds)];
}

function getScheduleTimezone(schedule: Schedule, serviceId: string): string | undefined {
  const timezoneExtension = getSchedulingParameters(schedule, serviceId)?.extension?.find(
    (extension) => extension.url === 'timezone'
  );
  return timezoneExtension?.valueCode ?? timezoneExtension?.valueString;
}

function collapseSlotEvents(events: SlotEvent[]): SlotEvent[] {
  if (events.length === 0) {
    return [];
  }

  const groupedSlots = new Map<string, SlotEvent[]>();

  for (const event of events) {
    const list = groupedSlots.get(event.status) ?? [];
    list.push(event);
    groupedSlots.set(event.status, list);
  }

  const collapsed: SlotEvent[] = [];

  for (const [status, statusSlots] of groupedSlots.entries()) {
    statusSlots.sort((a, b) => a.start.getTime() - b.start.getTime());

    let current = statusSlots[0];
    for (const slot of statusSlots.slice(1)) {
      if (slot.start <= new Date(current.end.getTime() + 1000)) {
        if (slot.end > current.end) {
          current.end = slot.end;
        }
        continue;
      }

      collapsed.push({
        ...current,
        title: status === 'free' ? 'Available' : 'Blocked',
        resource: { status },
      });
      current = slot;
    }

    collapsed.push({
      ...current,
      title: status === 'free' ? 'Available' : 'Blocked',
      resource: { status },
    });
  }

  return collapsed;
}
